import { useTableActionContext } from '@aibees/elephant/context';
import { useTrackEvents } from '@aibees/elephant/hooks';
import { useLabelDialogConfig } from '@aibees/elephant/hooks/labels/use-label-dialog-config';
import { useLabelGroupingStatus } from '@aibees/elephant/hooks/labels/use-label-grouping-status';
import { useSaveRecord } from '@aibees/elephant/hooks/use-save-record';
import { Label } from '@aibees/elephant/types/label';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useMemo, useState } from 'react';
import { useLabelManagementContext } from '../../search-table-action-dialog-lists/context';
import type { UseLabelActionParams, UseLabelActionReturn } from '../types';
import { useCurrentLabels } from './use-current-labels';
import { useLabelEntityConfig } from './use-label-entity-config';
import { useLabelItemsComparison } from './use-label-items-comparison';
import { useSearchType } from './use-search-type';

export function useLabelAction({
  type,
  currentLabelIds,
  id,
  searchTypeOverride,
  onLabelsSelect,
  selectedLabels: propsSelectedLabels,
}: UseLabelActionParams): UseLabelActionReturn {
  const user = useWorkspace()?.user;
  const notify = useNotify();
  const { track } = useTrackEvents();
  const { setLabels, setLists, labels, lists } = useTableActionContext();
  const { onSelect } = useLabelManagementContext();

  const searchType = useSearchType(searchTypeOverride);
  const grouping = useLabelGroupingStatus(searchTypeOverride);
  const api = useSaveRecord(searchType);

  const {
    isLabel,
    entityName,
    manageTitleDialog,
    emptyTitle,
    icon,
    createDialogType,
    notificationIcon,
  } = useLabelEntityConfig({ type, searchType });

  const { titleDialog, inputTitle, dialogIcon, placeholder } =
    useLabelDialogConfig({
      isLabel,
      searchTypeOverride: searchType,
    });

  const currentLabels = useCurrentLabels({
    type,
    currentLabelIds,
    propsSelectedLabels,
  });

  const items = useMemo(() => [...labels, ...lists], [labels, lists]);

  const {
    hasChanges,
    itemIds,
    allCurrentLabelsExist,
    allItemsExistInCurrentLabels,
    removedItemIds,
  } = useLabelItemsComparison({ currentLabels, items });

  const [selectedItems, setSelectedItems] = useState<Label[]>();
  const dialogState = useBoolean(false);
  const createDialogState = useBoolean(false);
  const loading = useBoolean(false);

  const handleOpenDialog = useCallback(() => {
    dialogState.onTrue();
    if (currentLabels.length > 0) {
      if (isLabel) {
        setLabels(currentLabels);
      } else {
        setLists(currentLabels);
      }
    }
  }, [currentLabels, isLabel, setLabels, setLists, dialogState]);

  const handleOpenCreateDialog = useCallback(() => {
    dialogState.onFalse();
    createDialogState.onTrue();
  }, [dialogState, createDialogState]);

  const handleCloseDialog = useCallback(() => {
    dialogState.onFalse();
    setLabels([]);
    setLists([]);
  }, [dialogState, setLabels, setLists]);

  const handleCloseCreateDialog = useCallback(() => {
    createDialogState.onFalse();
  }, [createDialogState]);

  const handleSetItems = useCallback(
    (v: Label[]) => {
      if (isLabel) setLabels(v);
      else setLists(v);
      setSelectedItems(v);
    },
    [isLabel, setLabels, setLists]
  );

  const handleSubmit = useCallback(() => {
    loading.onTrue();

    // If onLabelsSelect is provided (enrichment mode), use it
    if (onLabelsSelect) {
      onLabelsSelect(items);
      notify.success(
        `${isLabel ? 'Label' : 'List'}${
          items.length > 1 ? 's' : ''
        } successfully selected.`
      );
      dialogState.onFalse();
      setLabels([]);
      setLists([]);
      loading.onFalse();
      return;
    }

    // Otherwise, use normal API call
    const key = Date.now().toString();
    if (id) {
      if (allCurrentLabelsExist) {
        // If all currentLabels exist in items, only call ADD API once
        // (only new items were added, nothing was removed)
        api
          .mutateAsync({
            ids: [id],
            kind: 'ADD',
            labels: itemIds,
            user,
          })
          .then(() => {
            notify.complete(
              `${entityName} Updated`,
              `Your Selected ${entityName}s have been Updated Successfully.`,
              key,
              {
                icon: notificationIcon,
              }
            );
            track(isLabel ? 'ADD_LABEL_TO_RECORDS' : 'ADD_LIST_TO_RECORDS', {
              type,
              multiple: false,
              component: 'LabelAction',
            });
            dialogState.onFalse();
            setLabels([]);
            setLists([]);
          })
          .finally(() => loading.onFalse());
      } else if (allItemsExistInCurrentLabels && removedItemIds.length > 0) {
        api
          .mutateAsync({
            ids: [id],
            kind: 'REMOVE',
            labels: removedItemIds,
            user,
          })
          .then(() => {
            notify.complete(
              `${entityName} Updated`,
              `Your Selected ${entityName}s have been Updated Successfully.`,
              key,
              {
                icon: notificationIcon,
              }
            );
            track(isLabel ? 'ADD_LABEL_TO_RECORDS' : 'ADD_LIST_TO_RECORDS', {
              type,
              multiple: false,
              component: 'LabelAction',
            });
            dialogState.onFalse();
            setLabels([]);
            setLists([]);
          })
          .finally(() => loading.onFalse());
      } else {
        const itemsToAdd = itemIds;

        const removePromise =
          removedItemIds.length > 0
            ? api.mutateAsync({
                ids: [id],
                kind: 'REMOVE',
                labels: removedItemIds,
                user,
              })
            : Promise.resolve();

        const addPromise = api.mutateAsync({
          ids: [id],
          kind: 'ADD',
          labels: itemsToAdd,
          user,
        });

        Promise.all([removePromise, addPromise])
          .then(() => {
            notify.complete(
              `${entityName} Updated`,
              `Your Selected ${entityName}s have been Updated Successfully.`,
              key,
              {
                icon: notificationIcon,
              }
            );
            track(isLabel ? 'ADD_LABEL_TO_RECORDS' : 'ADD_LIST_TO_RECORDS', {
              type,
              multiple: false,
              component: 'LabelAction',
            });
            dialogState.onFalse();
            setLabels([]);
            setLists([]);
          })
          .finally(() => loading.onFalse());
      }
    } else {
      onSelect(items);
      dialogState.onFalse();
      loading.onFalse();
    }
  }, [
    loading,
    onLabelsSelect,
    id,
    items,
    itemIds,
    allCurrentLabelsExist,
    allItemsExistInCurrentLabels,
    removedItemIds,
    notify,
    isLabel,
    dialogState,
    setLabels,
    setLists,
    api,
    user,
    track,
    type,
    entityName,
    notificationIcon,
    onSelect,
  ]);

  const handleClearAll = useCallback(() => {
    setLabels([]);
    setLists([]);
  }, [setLabels, setLists]);

  return {
    // State
    isDialogOpen: dialogState.value,
    isCreateDialogOpen: createDialogState.value,
    isLoading: loading.value,
    currentLabels,
    selectedItems,
    hasChanges,

    // Entity config
    isLabel,
    entityName,
    manageTitleDialog,
    emptyTitle,
    icon,
    createDialogType,
    notificationIcon,

    // Dialog config
    titleDialog,
    inputTitle,
    dialogIcon,
    placeholder,

    // Search config
    searchType,
    grouping,

    // Handlers
    handleOpenDialog,
    handleCloseDialog,
    handleOpenCreateDialog,
    handleCloseCreateDialog,
    handleSetItems,
    handleSubmit,
    handleClearAll,

    // API state
    apiIsLoading: api.isLoading,
  };
}
